/**
 * THE INSPECTION SHEET.
 *
 * The PPI tool's shape — collapsible sections, an icon to find your place,
 * a count per section — over the shop's own depth of checks.
 *
 * ONE THUMB, UNDER A CAR. Every control is a target a hand can hit, the
 * sections start collapsed except the one being worked, and nothing needs
 * a keyboard until a note is owed. A sheet that needs two hands is a sheet
 * that gets filled in afterwards from memory, which is worse than no sheet
 * because it looks like evidence.
 *
 * SAVED AS YOU GO. No Save button, because there is no moment where the
 * work exists only on screen.
 */

import {
  openInspection, markPoint, setMileage, setNotes, whatIsLeft, finish, share,
  type Inspection, type Item,
} from "../data/inspections.ts";
import {
  SECTIONS, VERDICTS, NEEDS_REASON, REQUIRED_POINTS, treadVerdict, pointName,
  type Verdict,
} from "../domain/inspection-points.ts";
import { esc, mount } from "../ui/dom.ts";
import { jobPhotos } from "./photos.ts";
import type { Role } from "../domain/permissions.ts";
import { shopWords } from "../ui/words.ts";
import { cap } from "../domain/trade.ts";

let insp: Inspection | null = null;
let items = new Map<string, Item>();
let openSection = "engine";


/**
 * Every point starts as good, so counting answers is now counting to
 * itself. What a technician wants to know at a glance is where the
 * PROBLEMS are, so the section badge counts faults instead.
 */
function sectionFaults(id: string): { faults: number; total: number; measured: number } {
  const s = SECTIONS.find((x) => x.id === id)!;
  let faults = 0, measured = 0;
  for (const pt of s.points) {
    const it = items.get(pt.key);
    if (!it) continue;
    if (it.measurement != null) measured++;
    if (it.verdict === "watch" || it.verdict === "now") faults++;
  }
  return { faults, total: s.points.length, measured };
}

function pointRow(key: string, name: string): string {
  const it = items.get(key);
  const v = it?.verdict ?? null;
  const owesReason = v ? NEEDS_REASON.includes(v) : false;
  return `
    <div class="ipt" data-k="${esc(key)}">
      <div class="ipt-n">${esc(name)}</div>
      <div class="ipt-v">
        ${VERDICTS.map((x) => `
          <button class="vbtn v-${x.key} ${v === x.key ? "on" : ""}"
            data-k="${esc(key)}" data-v="${x.key}" title="${esc(x.what)}">${esc(x.short)}</button>`).join("")}
      </div>
      <div class="ipt-why" id="why-${esc(key)}" ${owesReason ? "" : "hidden"}>
        <div class="quiet small">Why — the customer reads this</div>
        <input class="ipt-note" data-k="${esc(key)}" maxlength="200"
          value="${esc(it?.note ?? "")}">
      </div>
    </div>`;
}

function tireRow(key: string, name: string): string {
  const it = items.get(key);
  const mm = it?.measurement ?? null;
  const t = mm != null ? treadVerdict(Number(mm)) : null;
  return `
    <div class="ipt tire" data-k="${esc(key)}">
      <div class="ipt-n">${esc(name)}</div>
      <div class="tire-in">
        <input class="ipt-tread" data-k="${esc(key)}" type="number" step="1" min="0" max="20"
          inputmode="numeric" value="${mm != null ? esc(String(mm)) : ""}">
        <span class="tire-u">/32</span>
        ${t ? `<span class="tire-say v-${t.verdict}">${esc(t.says)}</span>` : ""}
      </div>
    </div>`;
}

function sectionBlock(id: string): string {
  const s = SECTIONS.find((x) => x.id === id)!;
  const { faults, total, measured } = sectionFaults(id);
  const open = openSection === id;

  const badge = s.measured
    ? `<span class="isec-c ${measured ? "full" : ""}">${measured}/${total}</span>`
    : faults
      ? `<span class="isec-c fault">${faults} to look at</span>`
      : `<span class="isec-c full">all good</span>`;

  return `
    <div class="isec ${open ? "open" : ""}" data-s="${esc(id)}">
      <button class="isec-h" data-s="${esc(id)}">
        <span class="isec-i">${s.icon}</span>
        <span class="isec-t">${esc(s.title)}
          ${s.optional ? `<span class="pill">optional</span>` : ""}</span>
        ${badge}
      </button>
      <div class="isec-b">
        ${s.points.map((pt) => s.measured ? tireRow(pt.key, pt.name) : pointRow(pt.key, pt.name)).join("")}
      </div>
    </div>`;
}

export async function inspectionPage(jobId: string, role: Role = "viewer"): Promise<void> {
  if (role === "viewer") {
    mount(`<div class="page"><header class="page-head">
      <a class="back" href="#/job/${esc(jobId)}">&larr; The job</a><h1>Inspection</h1></header>
      <div class="card pad"><p class="quiet">Your login can look but not change.</p></div></div>`);
    return;
  }

  mount(`<div class="page"><div class="loading">Reading&hellip;</div></div>`);

  try {
    const sb = (await import("../data/jobs.ts")).getJob;
    const job = await sb(jobId);
    const got = await openInspection(jobId, job?.vehicles?.id ?? null);
    insp = got.inspection;
    items = got.items;
    draw(jobId, role,
      job?.vehicles ? `${job.vehicles.year ?? ""} ${job.vehicles.make ?? ""} ${job.vehicles.model ?? ""}`.trim() : "",
      job?.vehicles?.id ?? null);
  } catch (e) {
    mount(`<div class="page"><div class="empty bad">${
      esc(e instanceof Error ? e.message : String(e))}</div></div>`);
  }
}

function draw(jobId: string, role: Role, vehicle: string, vehicleId: string | null = null): void {
  const left = whatIsLeft(items);
  const faults = [...items.values()]
    .filter((i) => i.verdict === "watch" || i.verdict === "now").length;
  const ready = left.unanswered.length === 0 && left.unexplained.length === 0;

  mount(`<div class="page">
    <a class="back" href="#/job/${esc(jobId)}">&larr; The job</a>
    <header class="page-head">
      <h1>Inspection</h1>
      <p>${vehicle ? esc(vehicle) + " &middot; " : ""}saved as you go, so nothing is lost if you
         walk away. Tires are optional.</p>
    </header>

    <div class="iprog">
      <div class="iprog-n">${faults
        ? `<b>${faults}</b> ${faults === 1 ? "thing" : "things"} to tell them about`
        : `<b>Nothing</b> wrong so far`}</div>
      <div class="iprog-sub">All ${REQUIRED_POINTS.length} points start as good &mdash;
        mark only what is not.</div>
      ${left.unexplained.length ? `<div class="iprog-warn">${left.unexplained.length}
        ${left.unexplained.length === 1 ? "mark needs" : "marks need"} a reason:
        ${esc(left.unexplained.slice(0, 3).map(pointName).join(", "))}${
          left.unexplained.length > 3 ? "…" : ""}</div>` : ""}
    </div>

    <div class="card pad imeta">
      ${/* A door has no odometer. Hiding the field beats showing one
           nobody can fill in. Read into a local so the null check narrows
           it -- a second call to shopWords() would not.

           No placeholder: the label carries the word now, which is the
           rule the rest of this form already follows. */
        ((reading) => reading
          ? `<label class="field"><span>${esc(cap(reading))}</span>
        <input id="i-miles" type="number" inputmode="numeric"
          value="${insp?.mileage ?? ""}"></label>`
          : "")(shopWords().reading)}
      <label class="field"><span>Anything else worth saying</span>
        <input id="i-notes" maxlength="300"
          value="${esc(insp?.notes ?? "")}"></label>
    </div>

    <div class="isecs">${SECTIONS.map((s) => sectionBlock(s.id)).join("")}</div>

    <!-- Photos, on the sheet itself. A fault marked "needs doing now" with
         a picture beside it sells the work; the same fault as a line of
         text is a shop asking to be trusted. They file against the job
         like every other photo, so the customer's page shows them too. -->
    <div class="card" id="inspphotos"></div>

    <div class="card pad">
      <!-- A FINISHED INSPECTION IS STILL EDITABLE, AND NOW SAYS SO.
           Anthony: "we should be able to go back after the job is done and
           edit an inspection report."

           It always could be. Every point saves the moment it is marked
           whatever the job's status, and this page opens from the job card
           at any stage. What it did not do was SAY so: the screen read
           "Finished" over a button offering to finish it again, which is
           what a locked record looks like. Nobody edits a thing they
           believe is closed. -->
      ${insp?.finished_at
        ? `<p class="msg ok">Finished ${esc(new Date(insp.finished_at).toLocaleString())}.
             You can still change anything on it &mdash; every mark saves as you
             make it${insp.shared_at ? ", and the customer's copy updates with it" : ""}.</p>`
        : ""}
      <div class="ifin">
        <button class="btn primary" id="i-finish" ${ready ? "" : "disabled"}>
          ${insp?.finished_at ? "Save the changes" : "Finish the inspection"}</button>
        <button class="btn ${insp?.shared_at ? "" : "ghost"}" id="i-share">
          ${insp?.shared_at ? "Showing to the customer" : "Show it to the customer"}</button>
      </div>
      ${ready ? "" : `<p class="quiet">${left.unanswered.length
        ? `${left.unanswered.length} still to check.`
        : "Every mark that costs money needs a reason first."}</p>`}
      <div class="msg-slot" id="i-msg"></div>
    </div>
  </div>`);

  wire(jobId, role, vehicle);
  jobPhotos("inspphotos", jobId, vehicleId, role, "inspection");
}

function wire(jobId: string, role: Role, vehicle: string, vehicleId: string | null = null): void {
  const redraw = () => draw(jobId, role, vehicle, vehicleId);
  const say = (e: unknown) => {
    const m = document.getElementById("i-msg");
    if (m) m.innerHTML = `<p class="msg bad">${esc(e instanceof Error ? e.message : String(e))}</p>`;
  };

  document.querySelectorAll<HTMLButtonElement>(".isec-h").forEach((b) =>
    b.addEventListener("click", () => {
      openSection = openSection === b.dataset["s"] ? "" : (b.dataset["s"] ?? "");
      redraw();
    }));

  // A verdict saves the instant it is tapped. The row is patched rather
  // than the page redrawn, so tapping down a list does not fight you.
  document.querySelectorAll<HTMLButtonElement>(".vbtn").forEach((b) =>
    b.addEventListener("click", async () => {
      const key = b.dataset["k"]!;
      const v = b.dataset["v"] as Verdict;
      const before = items.get(key);
      // Touched, so it is no longer an assumption.
      const next: Item = {
        point_key: key, verdict: v,
        note: before?.note ?? null, measurement: null, unit: null,
      };
      items.set(key, next);

      b.parentElement?.querySelectorAll(".vbtn").forEach((x) => x.classList.remove("on"));
      b.classList.add("on");
      const why = document.getElementById(`why-${key}`);
      if (why) {
        if (NEEDS_REASON.includes(v)) why.removeAttribute("hidden");
        else why.setAttribute("hidden", "");
      }
      patchCounts();

      try { await markPoint(insp!.id, key, { verdict: v, note: next.note }); }
      catch (e) { items.set(key, before ?? next); say(e); }
    }));

  document.querySelectorAll<HTMLInputElement>(".ipt-note").forEach((el) =>
    el.addEventListener("change", async () => {
      const key = el.dataset["k"]!;
      const it = items.get(key);
      if (!it) return;
      it.note = el.value.trim() || null;
      try { await markPoint(insp!.id, key, { verdict: it.verdict, note: it.note }); patchCounts(); }
      catch (e) { say(e); }
    }));

  document.querySelectorAll<HTMLInputElement>(".ipt-tread").forEach((el) =>
    el.addEventListener("change", async () => {
      const key = el.dataset["k"]!;
      const mm = el.value === "" ? null : Number(el.value);
      const t = mm != null ? treadVerdict(mm) : null;
      items.set(key, {
        point_key: key, verdict: t?.verdict ?? "na",
        note: null, measurement: mm, unit: "32nds",
      });
      try {
        await markPoint(insp!.id, key,
          { verdict: t?.verdict ?? "na", measurement: mm, unit: "32nds" });
        redraw();
      } catch (e) { say(e); }
    }));

  document.getElementById("i-miles")?.addEventListener("change", async (e) => {
    const v = (e.target as HTMLInputElement).value;
    try { await setMileage(insp!.id, v === "" ? null : Number(v)); } catch (x) { say(x); }
  });
  document.getElementById("i-notes")?.addEventListener("change", async (e) => {
    try { await setNotes(insp!.id, (e.target as HTMLInputElement).value); } catch (x) { say(x); }
  });

  document.getElementById("i-finish")?.addEventListener("click", async () => {
    const b = document.getElementById("i-finish") as HTMLButtonElement;
    b.disabled = true; b.textContent = "Saving the sheet…";
    try {
      // Finishing writes every point that was only ever assumed good.
      // That is the moment the technician is saying they looked.
      await finish(insp!.id);
      insp!.finished_at = new Date().toISOString();
      redraw();
    } catch (e) {
      b.disabled = false;
      b.textContent = insp?.finished_at ? "Save the changes" : "Finish the inspection";
      say(e);
    }
  });
  document.getElementById("i-share")?.addEventListener("click", async () => {
    const on = !insp!.shared_at;
    try { await share(insp!.id, on); insp!.shared_at = on ? new Date().toISOString() : null; redraw(); }
    catch (e) { say(e); }
  });
}

/** The counters, without rebuilding the sheet under somebody's thumb. */
function patchCounts(): void {
  for (const s of SECTIONS) {
    const { faults, total, measured } = sectionFaults(s.id);
    const c = document.querySelector<HTMLElement>(`.isec[data-s="${s.id}"] .isec-c`);
    if (!c) continue;
    if (s.measured) {
      c.textContent = `${measured}/${total}`;
      c.classList.toggle("full", measured > 0);
      c.classList.remove("fault");
    } else {
      c.textContent = faults ? `${faults} to look at` : "all good";
      c.classList.toggle("fault", faults > 0);
      c.classList.toggle("full", faults === 0);
    }
  }

  const left = whatIsLeft(items);
  const faults = [...items.values()]
    .filter((i) => i.verdict === "watch" || i.verdict === "now").length;

  const n = document.querySelector<HTMLElement>(".iprog-n");
  if (n) {
    n.innerHTML = faults
      ? `<b>${faults}</b> ${faults === 1 ? "thing" : "things"} to tell them about`
      : `<b>Nothing</b> wrong so far`;
  }

  const fin = document.getElementById("i-finish") as HTMLButtonElement | null;
  if (fin) fin.disabled = !(left.unanswered.length === 0 && left.unexplained.length === 0);
}
